import { lazy } from 'react'
import { LazyRoute } from './route.type'
import { HicPages, HicReservationPages, HicSettingsPages, firstLevelPagesPermissions, reservationPagesPermissions, settingsPagesPermissions } from '../components/authorization/pages-permissions'

const SETTINGS_COMMON_PERMISSION = settingsPagesPermissions.get(HicSettingsPages.SETTINGS_COMMON_PERMISSION)

export const routes: LazyRoute[] = [
  {
    path: '/',
    Component: lazy(() => import('../pages/home/home.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.DASHBOARD),
  },
  {
    path: '/dashboard',
    Component: lazy(() => import('../pages/common/dashboard/dashboard.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.DASHBOARD),
  },
  {
    path: '/login',
    Component: lazy(() => import('../pages/login/login.page')),
    isPublic: true,
  },
  {
    path: '/trial/login',
    Component: lazy(() => import('../pages/login/login-trial.page')),
    isPublic: true,
  },
  //#region SETTINGS
  {
    path: '/settings',
    Component: lazy(() => import('../pages/settings/settings.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.SETTINGS),
  },
  {
    path: '/settings/privacy',
    Component: lazy(() => import('../pages/settings/privacy/privacy.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/cassaincloud',
    Component: lazy(() => import('../pages/settings/cassaincloud/cassaincloud.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/teamsystem/marketplace',
    Component: lazy(() => import('../pages/settings/marketplace/marketplace.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/vat',
    Component: lazy(() => import('../pages/settings/vat/vat.list.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/resource',
    Component: lazy(() => import('../pages/settings/resource/resource.list.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/position',
    Component: lazy(() => import('../pages/settings/position/position.list.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/chargecategory',
    Component: lazy(() => import('../pages/settings/chargecategory/chargecategory.list.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/resourcetype/:type',
    Component: lazy(() => import('../pages/settings/resource-type/resource-type-list.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/image-gallery',
    Component: lazy(() => import('../pages/settings/image-gallery/image-gallery.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/children',
    Component: lazy(() => import('../pages/settings/child-rules/child-age-ranges.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/user-roles',
    Component: lazy(() => import('../pages/settings/users-and-roles/user-roles.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/e-invoice',
    Component: lazy(() => import('../pages/settings/e-invoice/e-invoice.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/ps-configuration',
    Component: lazy(() => import('../pages/settings/ps-configuration/psConfiguration.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/istat-configuration',
    Component: lazy(() => import('../pages/settings/istat-configuration/istatConfiguration.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/citytax-configuration',
    Component: lazy(() => import('../pages/settings/citytax-configuration/cityTaxConfiguration.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/hotel-date',
    Component: lazy(() => import('../pages/settings/hotel-date/hotel-date.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/module-request',
    Component: lazy(() => import('../pages/settings/module-request/module-request.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/fiscal-gateway',
    Component: lazy(() => import('../pages/settings/fiscal-gateway/fiscal-gateway.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/tspay',
    Component: lazy(() => import('../pages/settings/tspay/tspay.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/booking-engine',
    Component: lazy(() => import('../pages/settings/booking-engine/booking-engine.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/booking-policy',
    Component: lazy(() => import('../pages/settings/booking-policy/booking-policy-list.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/mail-template-list',
    Component: lazy(() => import('../pages/settings/mail-templates/mail-template-list/mail-template-list.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/mail-template-versions-list/:code/:name',
    Component: lazy(() => import('../pages/settings/mail-templates/mail-template-versions-list/mail-template-versions-list.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/mail-template/new/:parentTemplateId/:language',
    Component: lazy(() => import('../pages/settings/mail-templates/mail-editor/mail-template-new.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/mail-template/edit/:parentTemplateId/:language',
    Component: lazy(() => import('../pages/settings/mail-templates/mail-editor/mail-template-edit.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/mail-template/:parentTemplateId/:language',
    Component: lazy(() => import('../pages/settings/mail-templates/mail-editor/mail-template-readonly.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/channels',
    Component: lazy(() => import('../pages/settings/channel/channel-list.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.CHANNELS),
  },
  {
    path: '/settings/channels/new',
    Component: lazy(() => import('../pages/settings/channel/channel-new.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.CHANNELS),
  },
  {
    path: '/settings/channels/:channelId',
    Component: lazy(() => import('../pages/settings/channel/channel-update.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.CHANNELS),
  },
  {
    path: '/settings/channels/airbnb/redirect',
    Component: lazy(() => import('../pages/settings/channel/channel-airbnb-redirect.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.CHANNELS),
  },
  {
    path: '/settings/product',
    Component: lazy(() => import('../pages/settings/products/products.list.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.CHARGES),
  },
  {
    path: '/settings/additional-services',
    Component: lazy(() => import('../pages/settings/additional-services/additional-service.list.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.CHARGES),
  },
  {
    path: '/common/rates',
    Component: lazy(() => import('../pages/common/rate/rate-list.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.RATES),
  },
  {
    path: '/common/rates/:rateId/:parentId',
    Component: lazy(() => import('../pages/common/rate/rate-update.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.RATES),
  },
  {
    path: '/common/rates/base-rate/new',
    Component: lazy(() => import('../pages/common/rate/rate-new-base-rate.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.RATES),
  },
  {
    path: '/common/rates/derived-rate/new/:parentId',
    Component: lazy(() => import('../pages/common/rate/rate-new-derived-rate.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.RATES),
  },
  {
    path: '/common/rates/static-rate/new/:parentId',
    Component: lazy(() => import('../pages/common/rate/rate-new-static-rate.page')),
    permissions: settingsPagesPermissions.get(HicSettingsPages.RATES),
  },
  {
    path: '/accounting/property/:propertyId',
    Component: lazy(() => import('../pages/accounting/property/property-update.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/accounting_data_export',
    Component: lazy(() => import('../pages/settings/accounting-data-export/accounting-data-export.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.SETTINGS),
  },
  {
    path: '/settings/payments-active',
    Component: lazy(() => import('../pages/settings/payments-active/payments-active.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/cash-desk-types',
    Component: lazy(() => import('../pages/settings/cash-desk-types/cash-desk-types.page')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/cash-desk-types/edit/:cashDeskTypeId',
    Component: lazy(() => import('../pages/settings/cash-desk-types/cash-desk-types.edit')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/cash-desk-types/new',
    Component: lazy(() => import('../pages/settings/cash-desk-types/cash-desk-types.new')),
    permissions: SETTINGS_COMMON_PERMISSION,
  },
  {
    path: '/settings/multi-management',
    Component: lazy(() => import('../pages/settings/multi-management/multi-management.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.SETTINGS),
  },
  {
    path: '/settings/billing',
    Component: lazy(() => import('../pages/settings/billing/billing.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.SETTINGS),
  },
  {
    path: '/settings/planning-configuration',
    Component: lazy(() => import('../pages/settings/planning/planning-configuration.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.SETTINGS),
  },
  //#endregion SETTINGS
  {
    path: '/common/customer',
    Component: lazy(() => import('../pages/common/customer/customer-list.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.CUSTOMERS),
  },
  {
    path: '/common/customer/person/new',
    Component: lazy(() => import('../pages/common/customer/customer-new-person.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.CUSTOMERS),
  },
  {
    path: '/common/customer/company/new',
    Component: lazy(() => import('../pages/common/customer/customer-new-company.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.CUSTOMERS),
  },
  {
    path: '/common/customer/:customerId',
    Component: lazy(() => import('../pages/common/customer/customer-update.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.CUSTOMERS),
  },
  {
    path: '/booking/reservations/:enumParam',
    Component: lazy(() => import('../pages/booking/reservation-list/reservation-list.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.RESERVATIONS),
  },
  {
    path: '/booking/reservation/:reservationId',
    Component: lazy(() => import('../pages/booking/reservation-detail/reservation-detail-wrapper')),
    permissions: reservationPagesPermissions.get(HicReservationPages.DETAILS),
  },
  {
    path: '/booking/planning',
    Component: lazy(() => import('../pages/booking/planning/planning.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.PLANNING),
  },
  {
    path: '/booking/planning-hourly',
    Component: lazy(() => import('../pages/booking/planning/planning-hourly.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.PLANNING),
  },
  {
    path: '/onboarding-property',
    Component: lazy(() => import('../pages/onboarding/property-onboarding.page')),
    hideMenu: true,
    hideToolbar: true,
  },
  {
    path: '/booking/prices-calendar',
    Component: lazy(() => import('../pages/booking/prices-calendar/prices-calendar.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.PRICES_CALENDAR),
  },

  {
    path: '/accounting/property/documents',
    Component: lazy(() => import('../pages/accounting/documents/documents-list.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.BILLS),
  },
  {
    path: '/develop',
    Component: lazy(() => import('../pages/dev.page')),
  },
  {
    path: '/style',
    Component: lazy(() => import('../pages/style-guide.page')),
  },
  {
    path: '/booking/passerby-bill-list',
    Component: lazy(() => import('../pages/booking/bill/passerby-bill.list.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.PASSERBY_BILLS),
  },
  {
    path: '/booking/pos',
    Component: lazy(() => import('../pages/booking/pos/pos.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.POS),
  },
  {
    path: '/pos',
    Component: lazy(() => import('../pages/booking/pos/pos.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.POS),
    disableMenu: true,
  },
  {
    path: '/booking/quotations-list',
    Component: lazy(() => import('../pages/quote/list/quote-list.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.QUOTATIONS),
  },
  {
    path: '/booking/quote/new',
    Component: lazy(() => import('../pages/quote/creation/quote.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.QUOTATIONS),
  },
  {
    path: '/booking/quote/:quotationId',
    Component: lazy(() => import('../pages/quote/edit/quote-edit.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.QUOTATIONS),
  },
  {
    path: '/booking/quote-read-only/:quotationId',
    Component: lazy(() => import('../pages/quote/consultation/quote-consultation.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.QUOTATIONS),
  },
  {
    path: '/common/statistics',
    Component: lazy(() => import('../pages/common/statistics/statistics.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.STATISTICS),
  },
  {
    path: '/sinapsi',
    Component: lazy(() => import('../pages/sinapsi/sinapsi.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.SINAPSI),
  },
  {
    path: '/missing_user_role',
    Component: lazy(() => import('../pages/missing-user-role.page')),
    isPublic: true,
  },
  {
    path: '/support',
    Component: lazy(() => import('../pages/support/support.page')),
  },
  {
    path: '*',
    Component: lazy(() => import('../pages/404.page')),
  },
  {
    path: '/housekeeping',
    Component: lazy(() => import('../pages/housekeeping/housekeeping.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.HOUSEKEEPING),
    disableMenu: true,
  },
  {
    path: '/internal-support',
    Component: lazy(() => import('../pages/internal-support/internal-support.page')),
    disableMenu: true,
  },
  {
    path: '/bills',
    Component: lazy(() => import('../pages/bills/bills.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.BILLS),
  },
  {
    path: '/messages',
    Component: lazy(() => import('../pages/messages/messages.page')),
    permissions: firstLevelPagesPermissions.get(HicPages.MESSAGES),
  },
  {
    path: '/audits',
    Component: lazy(() => import('../pages/audits/audits.page')),
    permissions: SETTINGS_COMMON_PERMISSION, // audits page is inside settings
  },
  {
    path: '/import-reservations',
    Component: lazy(() => import('../pages/import-reservations/import-reservations.page')),
    // TODO define permissions
  },
]
